import { AnimatePresence, motion } from 'motion/react'
import type { FC, ImgHTMLAttributes } from 'react'
import { useState } from 'react'
import { tv } from 'tailwind-variants'
import { isMatching } from 'ts-pattern'
import IcRoundExplicit from '~icons/ic/round-explicit'
import type { Media } from 'src/services/apollo/generated/graphql'
import { useBlurNsfw } from 'src/services/helpers/preference'
import { cx } from 'src/utils/cx'
import {
  getImagePublicUrl,
  getImageStillThumbnailUrl,
  getImageThumbnailUrl,
  getMediaUrl,
} from 'src/utils/media'
import { VideoMedia } from '../Media/VideoMedia'
import { Preloader } from '../Preloader/Preloader'
import BlurHash from './BlurHash'

export type MediaPreviewProps = JSX.IntrinsicElements['div'] & {
  mediaId?: string
  media?: Media
  videoMediaId?: string

  blurHash?: string
  thumbnail?: boolean
  preferStill?: boolean
  fit?: 'contain' | 'cover'
  /**
   * Custom object-position for cover mode.
   * When provided, overrides the default 'object-top' positioning.
   * Use this to focus on specific areas (e.g., faces) when cropping.
   */
  objectPosition?: string

  alt?: string
  imageLoading?: 'lazy' | 'eager'
  imageFetchPriority?: 'auto' | 'high' | 'low'

  /**
   * Some immature idea
   */
  experiments?: {
    /**
     * Built-in blur mask for flagged media
     *
     * Effectively: media.flag.shouldBlur && className="blur-xl"
     */
    safetyBlur?: boolean
  }
  /**
   * Props forwarded to the underlying `<img>`.
   * Supports `loading` and `fetchPriority`.
   */
  imgProps?: Pick<
    ImgHTMLAttributes<HTMLImageElement>,
    'loading' | 'fetchPriority'
  >
}

const mediaStyle = tv({
  base: 'w-full h-full',
  variants: {
    fit: {
      contain: 'object-contain',
      cover: 'object-cover',
    },
    fadeIn: {
      true: '',
    },
    loading: {
      true: 'opacity-0',
    },
    blurred: {
      true: 'blur-xl',
    },
  },
  compoundVariants: [
    {
      fadeIn: true,
      loading: false,
      className: 'transition-opacity duration-200 ease-out',
    },
  ],
  defaultVariants: {
    fit: 'cover',
  },
})

const MediaPreviewContent: FC<MediaPreviewProps> = ({
  mediaId,
  media,
  videoMediaId,
  blurHash,
  thumbnail,
  preferStill = false,
  fit = 'cover',
  objectPosition,
  alt,
  imageLoading = 'lazy',
  imageFetchPriority = 'auto',
  className,
  experiments,
  imgProps,
  ...props
}) => {
  const videoUrl = getMediaUrl(videoMediaId, 'orig')
  const hasVideo = !!videoUrl

  let displayImage
  if (thumbnail) {
    if (hasVideo && preferStill) {
      displayImage = getImageStillThumbnailUrl(mediaId, media)
    } else {
      displayImage = getImageThumbnailUrl(mediaId, media)
    }
  } else {
    displayImage = getImagePublicUrl(mediaId, media)
  }

  // Loading trackers
  const [loadingDisplayImg, setLoadingDisplayImg] = useState(false)
  const [loadingVideo, setLoadingVideo] = useState(false)
  const showPreloader = !preferStill && hasVideo && loadingVideo

  /*
   * Experiment: built-in safety blur
   */

  const [blurNsfw] = useBlurNsfw()
  const blurred = isMatching(
    {
      experiments: { safetyBlur: true },
      blurNsfw: true,
    },
    { experiments, blurNsfw, mediaId },
  )

  /*
   * Render
   */

  return (
    <figure
      {...props}
      className={cx(
        'group relative isolate bg-skeleton overflow-hidden',
        className || 'w-full h-full',
      )}
      // style={{
      //   aspectRatio: keepAspectRatio ? getAspectRatio(media) : undefined,
      // }}
    >
      {blurHash && (
        <BlurHash
          className="z-[-1] absolute inset-0 w-full h-full"
          hash={blurHash}
        />
      )}

      {displayImage && (
        <img
          alt={alt}
          loading={imgProps?.loading || imageLoading}
          fetchPriority={imgProps?.fetchPriority || imageFetchPriority}
          decoding="async"
          src={displayImage}
          className={mediaStyle({
            fit,
            fadeIn: !!blurHash,
            loading: loadingDisplayImg,
            blurred,
          })}
          style={{
            objectPosition:
              fit === 'cover' ? (objectPosition ?? 'top') : undefined,
          }}
          onLoadStart={() => setLoadingDisplayImg(true)}
          onLoad={() => setLoadingDisplayImg(false)}
        />
      )}

      {hasVideo && !preferStill && (
        <>
          {videoUrl && (
            <VideoMedia
              autoPlay
              muted
              src={videoUrl}
              className={mediaStyle({
                fit,
                fadeIn: !!blurHash && loadingDisplayImg,
                loading: loadingVideo,
                blurred,
                className: 'absolute inset-0',
              })}
              style={{
                objectPosition:
                  fit === 'cover' ? (objectPosition ?? 'top') : undefined,
              }}
              onLoadStart={() => setLoadingVideo(true)}
              onLoadedData={() => setLoadingVideo(false)}
            />
          )}

          <AnimatePresence>
            {showPreloader && (
              <motion.div
                className="absolute inset-0 bg-white/40 flex justify-center items-center"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: {
                    delay: 0.3,
                    duration: 0.2,
                    ease: 'easeOut',
                  },
                }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: 0.125,
                    ease: 'easeIn',
                  },
                }}
              >
                <Preloader />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {blurred && (
        <IcRoundExplicit className="absolute inset-0 m-auto text-3xl text-white opacity-50 group-hover:opacity-80 transition-opacity" />
      )}
    </figure>
  )
}

export default function MediaPreview(props: MediaPreviewProps) {
  // No `<Suspense>` wrapper here on purpose. With React 19's streaming
  // SSR (`renderToPipeableStream({ onShellReady })`), every `<Suspense>`
  // boundary that *could* defer is emitted as
  // `<!--$?--><template id="B:N"></template><!--/$-->` and the real
  // content goes into a trailing `<div hidden id="S:N">…</div>` that's
  // only swapped into place by a hydration script (`$RC("B:N","S:N")`).
  // For users browsing with JavaScript disabled — or simply during the
  // first paint window before scripts execute — that means the
  // recommended-artwork thumbnails ("更多精彩作品" grid under
  // `<ArtworkDetailAuthorArtworks>`) stay as five empty boxes even
  // though the SSR HTML already shipped the `<img>` markup. Wrapping
  // in `<Suspense>` here was unnecessary anyway: `<MediaPreviewContent>`
  // doesn't read any data hook that throws a promise (the i18n /
  // Apollo / oRPC hooks it transitively uses are either skipped on
  // SSR or run in non-suspense mode), so dropping the wrapper just
  // lets the markup land inline. If a future change introduces a
  // suspending hook here, add a *targeted* `<Suspense fallback={…}>`
  // around that hook's consumer rather than the whole preview.
  return <MediaPreviewContent {...props} />
}
